import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api, mediaUrl } from '@/lib/api';
import { Music as MusicIcon, Mic, FileText, Film, BookOpen } from 'lucide-react';

const KIND_ICON = {
  music: MusicIcon,
  podcast: Mic,
  article: FileText,
  video: Film,
  mobile_read: BookOpen,
};
const KIND_ACCENT = {
  music: 'emerald', podcast: 'emerald', article: 'gold', video: 'pink', mobile_read: 'gold',
};

function Rail({ title, kicker, items, testId }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-10" data-testid={testId}>
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-2.5 py-1">
            <p className="pg-text-glow-gold font-mono text-[10px] tracking-[0.32em]" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)' }}>{kicker}</p>
          </span>
          <div className="mt-1">
            <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-3 py-1.5">
              <h2 className="font-display text-2xl sm:text-3xl text-[var(--pg-ink)] tracking-[0.04em]" style={{ textShadow: '0 2px 18px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.95)' }}>{title}</h2>
            </span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.slice(0, 8).map((it, i) => {
          const Icon = KIND_ICON[it.kind] || FileText;
          const accent = KIND_ACCENT[it.kind] || 'emerald';
          return (
            <motion.div
              key={`${it.kind}-${it.id || i}`}
              initial={{ opacity: 1, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: (i % 4) * 0.04 }}
              className="pg-card overflow-hidden"
            >
              <Link to={it.href || '#'} className="block group" data-testid="home-section-card">
                <div className="aspect-square bg-[var(--pg-navy-800)] relative overflow-hidden">
                  {it.cover_url ? (
                    <img src={mediaUrl(it.cover_url)} alt={it.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center text-[var(--pg-${accent === 'emerald' ? 'emerald' : accent === 'gold' ? 'gold-2' : 'neon-pink'})]`}>
                      <Icon size={32} />
                    </div>
                  )}
                  {it.new_release && <span className="absolute top-2 left-2 pg-onair text-[9px]">NEW</span>}
                </div>
                <div className="p-3">
                  <p className="font-mono text-[10px] tracking-[0.28em] uppercase" style={{ color: accent === 'emerald' ? 'var(--pg-emerald)' : accent === 'gold' ? 'var(--pg-gold-2)' : 'var(--pg-neon-pink)' }}>{it.kind.replace('_', ' ')}</p>
                  <p className="font-serif text-sm mt-1 text-[var(--pg-ink)] line-clamp-2">{it.title}</p>
                  {it.subtitle && <p className="text-[11px] text-[var(--pg-ink)] mt-1 line-clamp-2">{it.subtitle}</p>}
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

export default function HomeSections() {
  const [sections, setSections] = useState(null);
  useEffect(() => { api.get('/home/sections').then(({ data }) => setSections(data)).catch(() => setSections({})); }, []);
  if (!sections) return null;
  return (
    <>
      <Rail title="NEW ON PLANET G" kicker="FRESH SIGNAL" items={sections.new_on_planet_g} testId="home-section-new" />
      <Rail title="FEATURED" kicker="HAND-PICKED BY G" items={sections.featured} testId="home-section-featured" />
      <Rail title="TRENDING" kicker="RISING" items={sections.trending} testId="home-section-trending" />
    </>
  );
}
